import * as THREE from 'three';
import Application from '../Application';
import ScreenMarquee from './ScreenMarquee';
import { heroScreen } from './SceneAnchors';
/**
 * Uniform scale applied to the model. The model authors it about 12 units
 * wide; the rest of the application (camera keyframes, monitor screen layers)
 * works in the ~1000s of units, so this brings the hero screen out to roughly
 * the 1280x1024 the CSS3D monitor was originally built around.
 */
const MODEL_SCALE = 1000;
/**
 * Which screen hosts the website iframe. `auto` scores every screen by its
 * world-space area weighted by how head-on it faces the default camera, which
 * breaks the five-way tie between the equally sized screens in favour of the
 * unrotated one in the middle. Override with a screen name such as
 * `Screen C.001` or `Screen D.001` — punctuation and case are ignored.
 */
const HERO_SCREEN = 'auto';
/** The direction the establishing shots look from, used for the auto tiebreak */
const VIEW_DIRECTION = new THREE.Vector3(0, 0.15, 1).normalize();
const MARQUEE_TEXT = 'WELCOME TO MY PORTFOLIO';
/** The screens are the light sources of the scene */
const SCREEN_LIGHT_COLOR = 0x4fffc0;
const SCREEN_LIGHT_INTENSITY = 2.9;
const SCREEN_LIGHT_RANGE = 3000;
/** How far in front of its screen each light sits */
const SCREEN_LIGHT_OFFSET = 260;
/** The website monitor casts warm light instead, so it reads as the odd one out */
const HERO_LIGHT_COLOR = 0xffa444;
const HERO_LIGHT_INTENSITY = 3.4;
const HERO_LIGHT_RANGE = 3200;
const normalizeName = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '');
export default class Computers {
    constructor() {
        this.application = new Application();
        this.scene = this.application.scene;
        this.resources = this.application.resources;
        this.time = this.application.time;
        this.setModel();
        this.screens = this.measureScreens();
        this.hero = this.pickHeroScreen();
        this.publishHeroScreen();
        this.marquee = new ScreenMarquee(MARQUEE_TEXT, this.hero.width / this.hero.height);
        this.dressScreens();
        this.setLighting();
    }
    setModel() {
        this.model = this.resources.items.gltfModel.computersModel.scene;
        this.model.scale.set(MODEL_SCALE, MODEL_SCALE, MODEL_SCALE);
        this.model.updateMatrixWorld(true);
        this.model.traverse((child) => {
            if (!(child instanceof THREE.Mesh))
                return;
            const material = child.material;
            if (material.name === 'led') {
                // Small indicator lights, always at full brightness
                child.material = new THREE.MeshBasicMaterial({
                    color: 0xffc98a,
                    toneMapped: false,
                });
                return;
            }
            if (material.name === 'Texture') {
                // Keep the glTF standard material so the cases are lit and
                // shaded by the screens, and smooth the shading out.
                material.roughness = 0.72;
                material.metalness = 0.05;
                material.flatShading = false;
                material.needsUpdate = true;
                child.geometry.computeVertexNormals();
            }
        });
        this.scene.add(this.model);
    }
    /**
     * Measures every screen quad in world space. The quads are flat planes in
     * their local XY with local +Z as the outward normal, so the world size and
     * orientation fall out of transforming three corners of the bounding box.
     */
    measureScreens() {
        const surfaces = [];
        this.model.traverse((child) => {
            if (!(child instanceof THREE.Mesh))
                return;
            if (child.material.name !== 'Screen')
                return;
            const geometry = child.geometry;
            if (!geometry.boundingBox)
                geometry.computeBoundingBox();
            const box = geometry.boundingBox;
            const center = box.getCenter(new THREE.Vector3());
            const origin = new THREE.Vector3(box.min.x, box.min.y, center.z);
            const alongX = new THREE.Vector3(box.max.x, box.min.y, center.z);
            const alongY = new THREE.Vector3(box.min.x, box.max.y, center.z);
            [origin, alongX, alongY, center].forEach((point) => point.applyMatrix4(child.matrixWorld));
            const quaternion = child.getWorldQuaternion(new THREE.Quaternion());
            surfaces.push({
                name: child.parent ? child.parent.name : child.name,
                mesh: child,
                position: center,
                quaternion,
                normal: new THREE.Vector3(0, 0, 1)
                    .applyQuaternion(quaternion)
                    .normalize(),
                width: alongX.distanceTo(origin),
                height: alongY.distanceTo(origin),
            });
        });
        return surfaces;
    }
    pickHeroScreen() {
        if (HERO_SCREEN !== 'auto') {
            const wanted = normalizeName(HERO_SCREEN);
            const match = this.screens.find((screen) => normalizeName(screen.name).startsWith(wanted));
            if (match)
                return match;
            console.warn(`[Computers] No screen matching "${HERO_SCREEN}", falling back to auto`);
        }
        // Several screens share the largest size, so weight the area by how
        // squarely the screen faces the camera to settle the tie.
        let best = this.screens[0];
        let bestScore = -Infinity;
        for (const screen of this.screens) {
            const facing = screen.normal.dot(VIEW_DIRECTION);
            const score = screen.width * screen.height * (0.55 + 0.45 * facing);
            if (score > bestScore) {
                bestScore = score;
                best = screen;
            }
        }
        return best;
    }
    publishHeroScreen() {
        heroScreen.position.copy(this.hero.position);
        heroScreen.quaternion.copy(this.hero.quaternion);
        heroScreen.normal.copy(this.hero.normal);
        heroScreen.width = this.hero.width;
        heroScreen.height = this.hero.height;
        heroScreen.ready = true;
    }
    /**
     * Hides the hero screen's own quad — MonitorScreen puts the iframe and its
     * CRT layers there instead — and gives every other screen the marquee.
     */
    dressScreens() {
        const remapped = new Set();
        for (const screen of this.screens) {
            if (screen === this.hero) {
                screen.mesh.visible = false;
                continue;
            }
            const geometry = screen.mesh.geometry;
            if (!remapped.has(geometry)) {
                this.remapScreenUVs(geometry);
                remapped.add(geometry);
            }
            screen.mesh.material = this.marquee.material;
        }
    }
    /**
     * The screen quads are UV mapped into the model's baked atlas, so the
     * marquee canvas would sample an arbitrary corner of it. Rebuild the UVs to
     * span the quad's own bounding box instead.
     */
    remapScreenUVs(geometry) {
        if (!geometry.boundingBox)
            geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const width = box.max.x - box.min.x || 1;
        const height = box.max.y - box.min.y || 1;
        const position = geometry.attributes.position;
        const uv = new Float32Array(position.count * 2);
        for (let i = 0; i < position.count; i++) {
            uv[i * 2] = (position.getX(i) - box.min.x) / width;
            uv[i * 2 + 1] = (position.getY(i) - box.min.y) / height;
        }
        geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    }
    /**
     * The room is lit almost entirely by the screens themselves: one point
     * light floating just in front of each one, plus a low ambient so the cases
     * facing away don't go completely black.
     */
    setLighting() {
        this.scene.add(new THREE.AmbientLight(0x16202a, 0.28));
        const hemisphere = new THREE.HemisphereLight(0x1d3a34, 0x060806, 0.22);
        hemisphere.position.set(0, 6000, 0);
        this.scene.add(hemisphere);
        for (const screen of this.screens) {
            const isHero = screen === this.hero;
            const light = new THREE.PointLight(isHero ? HERO_LIGHT_COLOR : SCREEN_LIGHT_COLOR, isHero ? HERO_LIGHT_INTENSITY : SCREEN_LIGHT_INTENSITY, isHero ? HERO_LIGHT_RANGE : SCREEN_LIGHT_RANGE, 2);
            light.position
                .copy(screen.position)
                .addScaledVector(screen.normal, SCREEN_LIGHT_OFFSET);
            this.scene.add(light);
        }
    }
    update() {
        this.marquee.update(this.time.delta);
    }
}
